/**
 * Hub /configuracoes/acessos — tela inicial de gestao de acessos.
 *
 * Layout (P3/P8/P11):
 *   [Topbar com refresh]
 *   [KPIs em strip — usuarios ativos, MFA, conflitos SoD, etc]
 *   [Proximas acoes recomendadas com tone-color]
 *   [Atividade recente — timeline]
 *
 * Endpoint agregado: GET /api/acessos/hub
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiRefreshCw, FiUserPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import AuditTimeline from "../../../components/AuditTimeline";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";

import { fetchHubAcessos } from "../api";
import {
  ActionsBar,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  ErrorMsg,
  PrimaryButton,
  SecondaryButton,
} from "../styles.jsx";

function HubAcessos() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  // O bloco de auditoria so aparece para quem tem `auditoria:view` (o
  // backend tambem so manda os eventos nesse caso).
  const veAuditoria = hasPermission("auditoria:view");
  const veSod = hasPermission("permissao:view");
  const [estado, setEstado] = useState({ loading: true, data: null, error: null });

  const carregar = useCallback(async () => {
    setEstado((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchHubAcessos(request);
      setEstado({ loading: false, data, error: null });
    } catch (err) {
      setEstado({ loading: false, data: null, error: err?.message || "Erro ao carregar." });
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = estado.data?.kpis || {};
  const proximas = estado.data?.proximas_acoes || [];
  const atividade = estado.data?.atividade_recente || [];

  const totalAtivos = kpis.usuarios_ativos || 0;
  const mfaHab = kpis.mfa_habilitado || 0;
  const mfaPct = totalAtivos ? Math.round((mfaHab / totalAtivos) * 100) : 0;

  const items = [
    {
      key: "ativos",
      label: "Usuários ativos",
      value: totalAtivos,
      tone: "default",
      href: "#",
      onClick: (e) => {
        e.preventDefault();
        navigate("/configuracoes/acessos/usuarios?ativo=true");
      },
    },
    {
      key: "mfa",
      label: "MFA habilitado",
      value: `${mfaHab}/${totalAtivos}`,
      sub: `${mfaPct}%`,
      tone: mfaPct < 50 ? "danger" : mfaPct < 80 ? "warning" : "success",
    },
    {
      key: "primeiros",
      label: "1º acesso pendente",
      value: kpis.primeiros_acessos_pendentes || 0,
      tone: (kpis.primeiros_acessos_pendentes || 0) > 0 ? "warning" : "default",
    },
    veSod && {
      key: "sod-ativos",
      label: "Conflitos SoD ativos",
      value: kpis.conflitos_sod_ativos || 0,
      tone: "info",
      href: "#",
      onClick: (e) => {
        e.preventDefault();
        navigate("/configuracoes/acessos/conflitos-sod");
      },
    },
    veSod && {
      key: "sod-violacoes",
      label: "Violações SoD",
      value: kpis.violacoes_sod_existentes || 0,
      tone: (kpis.violacoes_sod_existentes || 0) > 0 ? "danger" : "default",
    },
    veAuditoria && {
      key: "falhas",
      label: "Eventos de falha 24h",
      value: kpis.eventos_falha_24h || 0,
      tone: (kpis.eventos_falha_24h || 0) > 0 ? "danger" : "default",
    },
  ].filter(Boolean);

  if (estado.error) {
    // Mensagem mais util quando o backend nao tem o endpoint (404).
    // Acontece quando o frontend produciu foi deployado antes do backend.
    const ehBackendDesatualizado =
      String(estado.error).toLowerCase().includes("não encontrado") ||
      String(estado.error).toLowerCase().includes("404");

    return (
      <Card>
        <ErrorMsg>
          {ehBackendDesatualizado ? (
            <>
              <strong>O backend da API ainda não publicou os endpoints novos.</strong>{" "}
              Aguarde o deploy do servidor ou use a tela legada enquanto isso.
            </>
          ) : (
            estado.error
          )}
        </ErrorMsg>
        <ActionsBar>
          <SecondaryButton type="button" onClick={carregar}>
            <FiRefreshCw size={14} /> Tentar novamente
          </SecondaryButton>
          <SecondaryButton type="button" onClick={() => navigate("/configuracoes/acessos/legado")}>
            Ir para tela legada
          </SecondaryButton>
        </ActionsBar>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Visão geral</CardTitle>
            <CardSubtitle>
              KPIs do dia + ações recomendadas para revisão de acessos.
            </CardSubtitle>
          </div>
          <ActionsBar>
            <PrimaryButton type="button" onClick={() => navigate("/configuracoes/acessos/usuarios")}>
              <FiUserPlus size={14} /> Ir para usuários
            </PrimaryButton>
            <SecondaryButton type="button" onClick={carregar} disabled={estado.loading}>
              <FiRefreshCw size={14} /> Atualizar
            </SecondaryButton>
          </ActionsBar>
        </CardHeader>
        <KpiStrip items={items} />
      </Card>

      {proximas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Próximas ações recomendadas</CardTitle>
          </CardHeader>
          {proximas.map((acao) => (
            <Card
              key={acao.id}
              style={{
                background: "transparent",
                boxShadow: "none",
                border: "1px dashed",
              }}
            >
              <CardHeader>
                <div>
                  <CardTitle>{acao.titulo}</CardTitle>
                </div>
                <PrimaryButton type="button" onClick={() => navigate(acao.href)}>
                  {acao.cta}
                </PrimaryButton>
              </CardHeader>
            </Card>
          ))}
        </Card>
      )}

      {veAuditoria && (
        <Card>
          <CardHeader>
            <CardTitle>Atividade recente</CardTitle>
            <SecondaryButton type="button" onClick={() => navigate("/configuracoes/acessos/auditoria")}>
              Ver tudo
            </SecondaryButton>
          </CardHeader>
          <AuditTimeline
            events={atividade}
            onEventClick={(id) => navigate(`/configuracoes/acessos/auditoria?evento=${id}`)}
            emptyMessage="Sem atividade recente."
          />
        </Card>
      )}
    </>
  );
}

export default HubAcessos;
